import { useCallback, useEffect, useRef, useState } from "react";
import { api, ApiError } from "@/api/client";
import { READ_ONLY_TITLE, useReadOnly, useStore } from "@/state/store";
import { IconCheck, IconSave, IconSend } from "@/components/SvoIcons";
import type { RequirementFields, Team, Vote } from "@offsite/shared";
import { useWinningTopic } from "@/hooks/useWinningTopic";
import { useOffsiteConfig } from "@/engine/app/offsite-context";
import { useSitzungsZahlen } from "@/hooks/useSitzungsZahlen";
import { IdentityForm } from "@/workshop/IdentityForm";
import { QrCode } from "@/components/QrCode";

/**
 * Anrede pro Offsite. Das Vertriebs-Offsite duzt — Kollegen unter sich. Die
 * Gremien-Sitzung siezt: Ein Aufsichtsrat, der auf seinem Handy geduzt wird,
 * merkt sich das und nicht den Prototyp.
 */
const TEXTE = {
  du: {
    joinFehler:
      "Wir konnten dich keinem Team zuordnen. Ohne Team geht deine Eingabe verloren.",
    arbeitsplatz: "Einen Moment — wir richten deinen Arbeitsplatz ein …",
    nameLabel: "Dein Name",
    nameHinweis: "Erscheint über deinem Ergebnis auf der Leinwand. Vorname reicht.",
    namePlaceholder: "z. B. Martina",
    nameAutoComplete: "given-name",
    nachbessern: "Eingereicht — du kannst noch nachbessern, solange der Timer läuft.",
    tippt: "Du tippst — dein Team sieht den Stand live mit.",
    liveStand: "Du siehst den Stand live — auto-aktualisiert alle 3 Sekunden.",
    euerThema: "Euer Thema",
    eureAnforderung: "Eure Anforderung",
  },
  sie: {
    joinFehler:
      "Wir konnten Sie keinem Team zuordnen. Ohne Team geht Ihre Eingabe verloren.",
    arbeitsplatz: "Einen Moment — wir richten Ihren Arbeitsplatz ein …",
    nameLabel: "Ihr Name",
    nameHinweis: "Erscheint über Ihrem Ergebnis auf der Leinwand. Nachname genügt.",
    namePlaceholder: "z. B. Bergmann",
    nameAutoComplete: "family-name",
    nachbessern: "Eingereicht — Sie können noch nachbessern, solange der Timer läuft.",
    tippt: "Sie schreiben — der Stand wird laufend gesichert.",
    liveStand: "Sie sehen den Stand live — aktualisiert sich alle 3 Sekunden.",
    euerThema: "Ihr Thema",
    eureAnforderung: "Ihre Anforderung",
  },
} as const;
import type { RequirementFieldDef } from "@/engine/app/offsite-config";

const EMPTY: RequirementFields = {
  thema: "",
  was: "",
  fuerWen: "",
  problem: "",
  anforderungen: "",
  einschraenkungen: "",
  beispiel: "",
};

/**
 * `rows` ist bewusst NICHT Teil von RequirementFieldDef — die Zeilenhöhe ist
 * reine Darstellung und interessiert eine Offsite-Config nicht. Für die sechs
 * Vertriebs-Felder bewahrt sie aber das exakte bisherige Layout.
 */
type FieldDef = RequirementFieldDef & { rows?: number };

/**
 * Fallback-Schema: die sechs Vertriebs-Felder. Labels und Hinweistexte sind
 * wortgleich mit der früheren fest verdrahteten Liste — ein Offsite ohne
 * `requirementFields` sieht exakt aus wie vorher.
 */
const DEFAULT_FIELDS: FieldDef[] = [
  { key: "was", label: "Was soll gebaut werden?", placeholder: "2–3 Sätze", multiline: true, rows: 3 },
  { key: "fuerWen", label: "Für wen ist das?", placeholder: "Zielgruppe", multiline: true, rows: 2 },
  {
    key: "problem",
    label: "Welches Problem löst es?",
    placeholder: "Pain Point",
    multiline: true,
    rows: 3,
  },
  {
    key: "anforderungen",
    label: "Was muss es können?",
    placeholder: "Funktionale Anforderungen",
    multiline: true,
    rows: 3,
  },
  {
    key: "einschraenkungen",
    label: "Was darf es NICHT?",
    placeholder: "Leitplanken",
    multiline: true,
    rows: 2,
  },
  {
    key: "beispiel",
    label: "Beispiel-Szenario",
    placeholder: "Konkreter Anwendungsfall",
    multiline: true,
    rows: 3,
  },
];

/** Feldschema des aktiven Offsites — Config schlägt Default. */
function useRequirementFields(): FieldDef[] {
  const { requirementFields } = useOffsiteConfig();
  if (!requirementFields) return DEFAULT_FIELDS;
  return requirementFields.map((f) =>
    // Ein Feld, das mit dem Rahmenthema vorbelegt kommt, braucht zwei Zeilen:
    // einzeilig zeigte das Handy nur die ersten drei, vier Wörter, und was man
    // nicht lesen kann, kann man auch nicht sinnvoll ändern. Die Zeilenhöhe
    // bleibt dabei hier — sie ist Darstellung und interessiert eine
    // Offsite-Config nicht.
    f.prefillFromFraming && f.multiline ? { ...f, rows: 2 } : f,
  );
}

type SaveStatus = "idle" | "saving" | "saved" | "error";

/** Zeichenobergrenze je Feld im Stations-Ablauf; Zähler erst ab 500 sichtbar. */
const FELD_MAX = 600;
const FELD_ZAEHLER_AB = 500;

function isAllEmpty(f: RequirementFields): boolean {
  return (Object.keys(f) as (keyof RequirementFields)[]).every(
    (k) => !f[k] || !f[k].trim(),
  );
}

export function TeamWorkspace() {
  const { state } = useStore();
  const token = state.auth.token;
  const winner = useWinningTopic();
  const { soloTeams, formal, identity } = useOffsiteConfig();
  const texte = formal ? TEXTE.sie : TEXTE.du;

  const [vote, setVote] = useState<Vote | null>(null);
  const [team, setTeam] = useState<Team | null>(null);
  const [joinError, setJoinError] = useState<string | null>(null);
  const [joining, setJoining] = useState(false);
  const joinRequested = useRef(false);

  // Solo-Offsites (ein Teilnehmer = ein Team): Beitritt ist idempotent, der
  // Server gibt bei erneutem Aufruf dasselbe Team zurück.
  const joinSolo = useCallback(async () => {
    joinRequested.current = true;
    setJoining(true);
    setJoinError(null);
    try {
      const r = await api.teams.join(token);
      setTeam(r.team);
    } catch {
      // Bewusst laut: Ein stiller Fehlschlag hieße, dass jemand 25 Minuten
      // tippt und erst beim Einreichen merkt, dass er kein Team hat.
      setJoinError(texte.joinFehler);
    } finally {
      setJoining(false);
    }
  }, [token, texte.joinFehler]);

  useEffect(() => {
    if (!soloTeams || joinRequested.current) return;
    void joinSolo();
  }, [soloTeams, joinSolo]);

  // Poll vote + team every 3s. Captain reassignment / team-number change should be detected.
  useEffect(() => {
    if (soloTeams) return; // kein Voting, kein Team-Assignment
    let cancelled = false;
    const load = async () => {
      try {
        const v = await api.votes.me(token);
        if (cancelled) return;
        setVote(v.vote);
        const n = v.vote?.teamNumber;
        if (n) {
          try {
            const t = await api.teams.get(token, n);
            if (!cancelled) setTeam(t.team);
          } catch {
            /* team may not exist yet */
          }
        }
      } catch {
        /* noop */
      }
    };
    load();
    const id = setInterval(load, 3000);
    return () => {
      cancelled = true;
      clearInterval(id);
    };
  }, [token, soloTeams]);

  return (
    <section style={{ display: "grid", gap: 14 }}>
      {/* Timer wird vom MobileFrame-Top-Bar gerendert (kein doppelter Pill). */}
      {winner && (
        <div className="team-topic-banner">
          <div className="team-topic-banner-eyebrow">{texte.euerThema}</div>
          <div className="team-topic-banner-title">{winner.topic.title}</div>
        </div>
      )}
      {(() => {
        // Solo-Offsite: kein Voting, kein Schriftführer — direkt in den Editor.
        if (soloTeams) {
          if (team) {
            // Pflicht-Identität (Code-Login): ohne Name + Abteilung gibt es
            // niemanden, an den der dauerhafte Ergebnis-Link hängt — also
            // erst das Formular, dann der Editor.
            if (identity && (!team.name || !team.abteilung)) {
              return (
                <IdentityForm n={team.number} onDone={(t) => setTeam(t)} />
              );
            }
            return (
              <CaptainEditor
                n={team.number}
                teamName={team.name}
                resultId={team.resultId}
              />
            );
          }
          return (
            <div
              style={{
                display: "grid",
                gap: 10,
                textAlign: "center",
                padding: "2rem 0",
                justifyItems: "center",
              }}
            >
              {joinError ? (
                <>
                  <p className="form-error" role="alert" style={{ margin: 0 }}>
                    {joinError}
                  </p>
                  <button
                    type="button"
                    onClick={() => void joinSolo()}
                    disabled={joining}
                    className="btn btn-primary btn-lg"
                  >
                    {joining ? "…" : "Erneut versuchen"}
                  </button>
                </>
              ) : (
                <p className="deck-sub" style={{ margin: 0 }} role="status">
                  {texte.arbeitsplatz}
                </p>
              )}
            </div>
          );
        }

        // State 1: No team yet.
        if (!vote || !vote.teamNumber) {
          return (
            <div
              style={{
                display: "grid",
                gap: 8,
                textAlign: "center",
                padding: "2rem 0",
              }}
            >
              <div className="deck-eyebrow">Noch kein Team</div>
              <p className="deck-sub" style={{ margin: 0 }}>
                Du wurdest noch keinem Team zugeteilt. Frag den Moderator.
              </p>
            </div>
          );
        }

        const n = vote.teamNumber;
        const isCaptain = vote.isCaptain === true;
        const teamName = team?.name ?? null;

        // State 2: Captain WITHOUT team name yet.
        if (isCaptain && !teamName) {
          return (
            <CaptainNameForm
              n={n}
              onNamed={(updated) => setTeam(updated)}
            />
          );
        }

        // State 3: Captain WITH team name → editor.
        if (isCaptain && teamName) {
          return <CaptainEditor n={n} teamName={teamName} />;
        }

        // State 4: Non-captain viewer.
        return <ViewerView n={n} teamName={teamName} />;
      })()}
    </section>
  );
}

function CaptainNameForm({
  n,
  onNamed,
}: {
  n: number;
  onNamed: (team: Team) => void;
}) {
  const { state } = useStore();
  const token = state.auth.token;
  const readOnly = useReadOnly();
  const [name, setName] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  const submit = async () => {
    const trimmed = name.trim();
    if (!trimmed) {
      setErr("Bitte gib einen Teamnamen ein.");
      return;
    }
    setErr(null);
    setSubmitting(true);
    try {
      const r = await api.teams.setName(token, n, trimmed);
      onNamed(r.team);
    } catch {
      setErr("Konnte Teamnamen nicht speichern.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div style={{ display: "grid", gap: 14 }}>
      <header>
        <div className="deck-eyebrow">Team {n}</div>
        <h2
          className="deck-title"
          style={{ fontSize: "clamp(1.4rem, 4.5vw, 1.85rem)", margin: "4px 0 0" }}
        >
          Du bist Schriftführer von Team {n}
        </h2>
        <p className="deck-sub" style={{ marginTop: 8 }}>
          Gib zuerst einen Teamnamen ein, damit wir uns gut zuordnen können.
        </p>
      </header>

      <input
        className="input-text"
        type="text"
        value={name}
        placeholder="z.B. Die Kreativen"
        onChange={(e) => setName(e.target.value)}
        onKeyDown={(e) => {
          if (e.key === "Enter") {
            e.preventDefault();
            submit();
          }
        }}
        autoFocus
        disabled={submitting}
      />

      {err && <p className="form-error">{err}</p>}

      <button
        type="button"
        onClick={submit}
        disabled={submitting || !name.trim() || readOnly}
        className={`btn btn-primary btn-block btn-lg${readOnly ? " is-readonly" : ""}`}
        title={readOnly ? READ_ONLY_TITLE : undefined}
      >
        → Teamname festlegen & loslegen
      </button>

      <p className="form-hint" style={{ textAlign: "center" }}>
        Du wurdest zufällig ausgewählt — wenn du nicht magst, frag den Moderator
        nach Tausch.
      </p>
    </div>
  );
}

/**
 * Was nach dem Einreichen als nächstes passiert — abhängig vom Ablauf des
 * Offsites, nicht geraten.
 *
 * Das Vertriebs-Offsite lässt die KI erst bewerten und baut später über Nacht.
 * Die Gremien-Sitzung hat gar keine Bewertungsphase (`phases` kennt kein
 * "evaluating"); dort stand trotzdem „die KI bewertet gleich" auf dem Handy —
 * eine Ankündigung, die nie eintrat.
 */
function nachDemEinreichenText(phases: readonly string[]): string {
  return phases.includes("evaluating")
    ? "Eingereicht — die KI bewertet gleich."
    : "Eingereicht — die KI baut daraus gleich einen Prototyp.";
}

function CaptainEditor({
  n,
  teamName,
  resultId,
}: {
  n: number;
  teamName: string | null;
  resultId?: string | null;
}) {
  const fieldDefs = useRequirementFields();
  const { soloTeams, formal, phases, einzelmodus, stationsAblauf } =
    useOffsiteConfig();
  const basisTexte = formal ? TEXTE.sie : TEXTE.du;
  // Einzelmodus: Es gibt kein Team, das mitliest — Plural und „dein Team"
  // wären hier gelogen und verwirren den, der allein auf sein Handy schaut.
  const texte = einzelmodus
    ? {
        ...basisTexte,
        eureAnforderung: formal ? "Ihre Anforderung" : "Deine Anforderung",
        tippt: formal
          ? "Sie tippen — alles wird automatisch gespeichert."
          : "Du tippst — alles wird automatisch gespeichert.",
      }
    : basisTexte;
  const nachDemEinreichen = nachDemEinreichenText(phases);
  const framingField = fieldDefs.find((f) => f.prefillFromFraming) ?? null;
  const [framingTopic, setFramingTopic] = useState<string>("");
  const { state } = useStore();
  const token = state.auth.token;
  const readOnly = useReadOnly();
  const phase = state.workshop.phase;
  const endsAt = phase.timerEndsAt;
  // Re-render every second so the "kann noch bearbeiten"-window updates live.
  const [, setTick] = useState(0);
  useEffect(() => {
    const id = setInterval(() => setTick((n) => n + 1), 1000);
    return () => clearInterval(id);
  }, []);
  const timerStillRunning =
    typeof endsAt === "number" && endsAt > Date.now();
  // Nachbessern geht, solange die Phase "working" ist — auch nach Ablauf der
  // Uhr (Briefing Prompt-Teil 6: erst der Wechsel auf Generierung beendet das
  // Einreichen). Der Server erlaubt Reopen entsprechend.
  const canReopen = phase.phase === "working";

  /**
   * Getippt wird erst, wenn die Zeit läuft.
   *
   * Der Ablauf hat zwei Schritte in der Phase „working": „Prompten öffnen" zeigt
   * dem Gremium die drei Fragen, „Arbeitsphase" startet die acht Minuten. Wären
   * die Felder schon im ersten Schritt beschreibbar, tippten die ersten los,
   * während vorne noch erklärt wird — und die Zeit gilt dann nicht für alle
   * gleich.
   *
   * Offsites ohne Timer-Schritt (Vertrieb) sind nicht betroffen: dort läuft die
   * Uhr, sobald die Arbeitsphase beginnt.
   */
  const pausiert =
    typeof phase.timerPausedRemainingSec === "number" &&
    phase.timerPausedRemainingSec > 0;
  // Gesperrt ist nur „Prompten offen, Uhr noch nie gestartet". Eine
  // abgelaufene Uhr sperrt NICHT mehr (Briefing Prompt-Teil 6): Wer noch
  // tippt, darf einreichen, bis die Moderation auf Generierung schaltet —
  // erst der Phasenwechsel nimmt das Formular weg.
  const uhrLiefSchon =
    typeof endsAt === "number" ||
    pausiert ||
    typeof phase.timerBeendetAt === "number";
  const nochNichtTippen = soloTeams && !uhrLiefSchon;

  const [fields, setFields] = useState<RequirementFields>(EMPTY);
  const [loaded, setLoaded] = useState(false);
  const [status, setStatus] = useState<"draft" | "submitted">("draft");
  const [saveStatus, setSaveStatus] = useState<SaveStatus>("idle");
  const [submitErr, setSubmitErr] = useState<string | null>(null);
  const [reopening, setReopening] = useState(false);
  const pending = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Wer abgeschickt hat, saß bisher vor einem stummen Bildschirm und wusste
  // nicht, ob er auf die anderen wartet oder ob etwas kaputt ist. Läuft nur
  // nach dem Einreichen — vorher soll die Zahl nicht vom Tippen ablenken.
  const zahlen = useSitzungsZahlen(status === "submitted", state.auth.token);

  // Initial load only — captain is the source of truth from this point.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const r = await api.teams.getReq(token, n);
        if (cancelled) return;
        setFields(r.requirement.fields);
        setStatus(r.requirement.status);
      } catch (err) {
        if (err instanceof ApiError && err.status === 404) {
          // No requirement yet → keep EMPTY.
        }
      } finally {
        if (!cancelled) setLoaded(true);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [token, n]);

  // Rahmenthema nur holen, wenn ein Feld es überhaupt braucht.
  const wantsFraming = framingField !== null;
  useEffect(() => {
    if (!wantsFraming) return;
    let cancelled = false;
    (async () => {
      try {
        const r = await api.framing.get(token);
        if (!cancelled) setFramingTopic(r.topic ?? "");
      } catch {
        /* ohne Rahmenthema bleibt das Feld eben leer */
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [token, wantsFraming]);

  // Vorbelegung ausschließlich in ein leeres Feld — und erst, wenn der
  // gespeicherte Stand da ist. Sonst überschreibt ein nachgeladenes
  // Rahmenthema die eigene Idee des Teilnehmers.
  useEffect(() => {
    if (!framingField || !framingTopic || !loaded) return;
    setFields((prev) =>
      prev[framingField.key]
        ? prev
        : { ...prev, [framingField.key]: framingTopic },
    );
  }, [framingField, framingTopic, loaded]);

  // Solo-Offsites (AR): Statt einer anonymen Teamnummer trägt jeder seinen
  // Namen ein. Er erscheint über dem eigenen Prototyp auf der Leinwand.
  // Bewusst optional und ohne eigenen Schritt davor — wer nichts einträgt,
  // bleibt "Team 3", und niemand verliert Zeit vor dem Prompten.
  const [nameEntwurf, setNameEntwurf] = useState<string>(teamName ?? "");
  const [nameStatus, setNameStatus] = useState<SaveStatus>("idle");
  const nameTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    if (teamName) setNameEntwurf(teamName);
  }, [teamName]);

  const speichereName = (wert: string) => {
    setNameEntwurf(wert);
    if (nameTimer.current) clearTimeout(nameTimer.current);
    const sauber = wert.trim();
    if (!sauber) return; // leeren Namen nicht speichern — bleibt Team N
    setNameStatus("saving");
    nameTimer.current = setTimeout(async () => {
      try {
        await api.teams.setName(token, n, sauber);
        setNameStatus("saved");
      } catch {
        // Sichtbar scheitern statt still: sonst wundert sich jemand auf der
        // Leinwand, warum sein Name fehlt.
        setNameStatus("error");
      }
    }, 700);
  };

  const scheduleSave = (next: RequirementFields) => {
    if (pending.current) clearTimeout(pending.current);
    if (isAllEmpty(next)) return; // don't autosave a blank slate
    setSaveStatus("saving");
    pending.current = setTimeout(async () => {
      try {
        await api.teams.putReq(token, n, next);
        setSaveStatus("saved");
      } catch {
        setSaveStatus("error");
      }
    }, 600);
  };

  const update = (key: keyof RequirementFields, value: string) => {
    if (status === "submitted") return;
    const next = { ...fields, [key]: value };
    setFields(next);
    scheduleSave(next);
  };

  const submit = async () => {
    setSubmitErr(null);
    try {
      // Immer flushen: Eine Vorbelegung (Rahmenthema) erzeugt keinen
      // Autosave-Timer, wäre ohne diesen Put also nie gespeichert.
      if (pending.current) {
        clearTimeout(pending.current);
        pending.current = null;
      }
      if (!isAllEmpty(fields)) {
        await api.teams.putReq(token, n, fields);
      }
      await api.teams.submitReq(token, n);
      setStatus("submitted");
    } catch {
      setSubmitErr("Einreichung fehlgeschlagen.");
    }
  };

  const reopen = async () => {
    setReopening(true);
    setSubmitErr(null);
    try {
      const r = await api.teams.reopenReq(token, n);
      setFields(r.requirement.fields);
      setStatus(r.requirement.status);
    } catch {
      setSubmitErr("Bearbeitung konnte nicht wieder freigeschaltet werden.");
    } finally {
      setReopening(false);
    }
  };

  const submitted = status === "submitted";

  return (
    <div style={{ display: "grid", gap: 14 }}>
      <header style={{ display: "grid", gap: 4 }}>
        {/* Einzelmodus: keine Teamnummer — die Person meldet sich per Code
            an und hat kein Team-Wording auf dem Handy. */}
        {!einzelmodus && (
          <div className="deck-eyebrow">
            Team {n}
            {teamName ? ` · ${teamName}` : ""}
          </div>
        )}
        {soloTeams && !einzelmodus && !readOnly && (
          <div className="req-field" style={{ marginTop: 6 }}>
            <label className="req-field-label" htmlFor="ar-name">
              {texte.nameLabel}
            </label>
            <span className="req-field-hint">
              {texte.nameHinweis}
            </span>
            <input
              id="ar-name"
              className="input-text"
              type="text"
              autoComplete={texte.nameAutoComplete}
              maxLength={40}
              value={nameEntwurf}
              placeholder={texte.namePlaceholder}
              onChange={(e) => speichereName(e.target.value)}
              disabled={submitted}
            />
            {nameStatus === "error" && (
              <span className="autosave-status is-error" role="alert">
                Name konnte nicht gespeichert werden
              </span>
            )}
          </div>
        )}
        <div
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            gap: 10,
            flexWrap: "wrap",
          }}
        >
          <h2
            className="deck-title"
            style={{
              fontSize: "clamp(1.4rem, 4.5vw, 1.85rem)",
              margin: "4px 0 0",
            }}
          >
            {texte.eureAnforderung}
          </h2>
          {submitted ? (
            <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
              <span className="autosave-status is-saved">
                <IconCheck size={14} />
                Eingereicht
              </span>
              {canReopen && (
                <button
                  type="button"
                  onClick={reopen}
                  disabled={reopening || readOnly}
                  className={`btn btn-ghost${readOnly ? " is-readonly" : ""}`}
                  title={readOnly ? READ_ONLY_TITLE : undefined}
                  style={{ padding: "4px 10px", fontSize: 13 }}
                >
                  {reopening ? "…" : "↺ Erneut bearbeiten"}
                </button>
              )}
            </div>
          ) : !loaded ? (
            <span
              className="captain-editor-loading-banner"
              role="status"
              aria-live="polite"
            >
              Lade gespeicherten Stand …
            </span>
          ) : (
            <AutosavePill status={saveStatus} />
          )}
        </div>
        <p className="deck-sub" style={{ marginTop: 8 }}>
          {submitted
            ? canReopen
              ? texte.nachbessern
              : nachDemEinreichen
            : texte.tippt}
        </p>
        {submitted && zahlen && zahlen.angemeldet > 0 && (
          <p className="req-runde-stand" role="status" data-testid="runde-stand">
            {zahlen.eingereicht} von {zahlen.angemeldet} sind fertig
            {zahlen.eingereicht < zahlen.angemeldet && " — wir warten noch"}
          </p>
        )}
      </header>

      {/* Der dauerhafte, persönliche Link gehört direkt nach dem Einreichen
          aufs Handy: Der Strategietag-Ablauf kennt keine globale
          submitted-Phase, in der MobileSubmittedHero ihn zeigen würde — wer
          ihn jetzt nicht abfotografiert, findet sein Ergebnis später nicht. */}
      {submitted && einzelmodus && resultId && (
        <div className="req-result-link" data-testid="result-link">
          <p className="req-field-label">
            {formal ? "Ihr persönlicher Link" : "Dein persönlicher Link"}
          </p>
          <p className="req-field-hint">
            {formal
              ? "Unter diesem Link finden Sie Ihren Prototyp dauerhaft — fotografieren Sie den Code einfach ab."
              : "Unter diesem Link findest du deinen Prototyp dauerhaft — fotografier den Code einfach ab."}
          </p>
          <QrCode value={`${window.location.origin}/ergebnis/${resultId}`} klein />
          <p className="mono req-result-url">
            {`${window.location.host}/ergebnis/${resultId}`}
          </p>
        </div>
      )}

      {nochNichtTippen && !submitted && (
        <p className="req-noch-warten" role="status" data-testid="req-noch-warten">
          {/* Der Text war hart gesiezt — im Du-Offsite fiel er als einziger
              Sie-Satz sofort auf. */}
          {formal
            ? "Lesen Sie ruhig schon mit — schreiben können Sie, sobald die Zeit läuft."
            : "Lies ruhig schon mit — schreiben kannst du, sobald die Zeit läuft."}
        </p>
      )}

      {/* „Noch eine Minute — jetzt einreichen." unter 60 s Restzeit
          (Briefing Prompt-Teil 4.3). Nur solange die Uhr wirklich läuft. */}
      {stationsAblauf &&
        !submitted &&
        timerStillRunning &&
        typeof endsAt === "number" &&
        endsAt - Date.now() < 60_000 && (
          <p className="req-noch-warten" role="status" data-testid="req-letzte-minute">
            Noch eine Minute — jetzt einreichen.
          </p>
        )}

      {fieldDefs.map(({ key, label, placeholder, multiline, rows, hint }, i) => (
        <label
          key={key}
          className={`req-field rise rise-${Math.min(i + 1, 6)}${!loaded ? " req-field-skeleton" : ""}`}
        >
          <span className="req-field-label">{label}</span>
          {/* Dauerhaft sichtbare Erklärzeile — kein Tooltip. Für Teilnehmer,
              die zum ersten Mal mit einer KI arbeiten, ist die Führung Teil
              der Aufgabe und darf nicht hinter einer Interaktion liegen. */}
          {hint && <span className="req-field-hint">{hint}</span>}
          {/* Placeholder-Zeile nur ohne echten Hint — sonst stehen zwei
              Erklärzeilen übereinander (Review-Fund 14.09.). */}
          {!hint && <span className="req-field-hint">{placeholder}</span>}
          {multiline ? (
            <textarea
              className="input-textarea"
              value={fields[key] ?? ""}
              onChange={(e) => update(key, e.target.value)}
              readOnly={submitted || !loaded || readOnly || nochNichtTippen}
              rows={rows ?? 3}
              maxLength={stationsAblauf ? FELD_MAX : undefined}
              aria-busy={!loaded}
              placeholder={!loaded ? "Lade …" : undefined}
              title={readOnly ? READ_ONLY_TITLE : undefined}
            />
          ) : (
            <input
              type="text"
              className="input-text"
              value={fields[key] ?? ""}
              onChange={(e) => update(key, e.target.value)}
              readOnly={submitted || !loaded || readOnly || nochNichtTippen}
              maxLength={stationsAblauf ? FELD_MAX : undefined}
              aria-busy={!loaded}
              placeholder={!loaded ? "Lade …" : undefined}
              title={readOnly ? READ_ONLY_TITLE : undefined}
            />
          )}
          {/* Zähler erst ab 500 Zeichen — vorher ist er nur Rauschen. */}
          {stationsAblauf && (fields[key]?.length ?? 0) >= FELD_ZAEHLER_AB && (
            <span className="req-field-hint" data-testid={`zaehler-${key}`}>
              {fields[key]?.length ?? 0} / {FELD_MAX}
            </span>
          )}
        </label>
      ))}

      {submitErr && <p className="form-error">{submitErr}</p>}

      {!submitted && (
        <button
          type="button"
          onClick={submit}
          // Stations-Ablauf: aktiv ab dem ersten Zeichen in Feld 1 — die
          // Felder 2 und 3 sind optional („lieber ein halber Prompt als
          // keiner", Briefing 6). Sonst wie bisher: irgendein Feld genügt.
          // In der Prompten-Phase (Felder gesperrt) auch das Einreichen
          // sperren — sonst schickt ein versehentlicher Tipp den unbearbeiteten
          // Themen-Prefill ab (Review-Fund 14.09.).
          disabled={
            (stationsAblauf
              ? !fields[fieldDefs[0]?.key ?? "was"]?.trim()
              : isAllEmpty(fields)) ||
            readOnly ||
            nochNichtTippen
          }
          className={`btn btn-success btn-block btn-lg${readOnly ? " is-readonly" : ""}`}
          title={readOnly ? READ_ONLY_TITLE : undefined}
        >
          <IconSend size={18} />
          Anforderung einreichen
        </button>
      )}
    </div>
  );
}

function AutosavePill({ status }: { status: SaveStatus }) {
  if (status === "idle") return null;
  if (status === "saving") {
    return (
      <span className="autosave-status is-saving">
        <IconSave size={14} />
        Speichert …
      </span>
    );
  }
  if (status === "saved") {
    return (
      <span className="autosave-status is-saved">
        <IconCheck size={14} />
        gespeichert
      </span>
    );
  }
  return (
    <span className="autosave-status is-error">
      Speicherfehler
    </span>
  );
}

function ViewerView({ n, teamName }: { n: number; teamName: string | null }) {
  const { formal, phases } = useOffsiteConfig();
  const nachDemEinreichen = nachDemEinreichenText(phases);
  const texte = formal ? TEXTE.sie : TEXTE.du;
  const fieldDefs = useRequirementFields();
  const { state } = useStore();
  const token = state.auth.token;
  const [fields, setFields] = useState<RequirementFields | null>(null);
  const [status, setStatus] = useState<"draft" | "submitted" | null>(null);

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      try {
        const r = await api.teams.getReq(token, n);
        if (cancelled) return;
        setFields(r.requirement.fields);
        setStatus(r.requirement.status);
      } catch (err) {
        if (err instanceof ApiError && err.status === 404) {
          if (!cancelled) {
            setFields(null);
            setStatus(null);
          }
        }
      }
    };
    load();
    const id = setInterval(load, 3000);
    return () => {
      cancelled = true;
      clearInterval(id);
    };
  }, [token, n]);

  const headerTitle = `Team ${n}${teamName ? " · " + teamName : ""} — Schriftführer schreibt`;

  return (
    <div style={{ display: "grid", gap: 14 }}>
      <header>
        <div className="deck-eyebrow">Live-Ansicht</div>
        <h2
          className="deck-title"
          style={{ fontSize: "clamp(1.3rem, 4vw, 1.7rem)", margin: "4px 0 0" }}
        >
          {headerTitle}
        </h2>
        <p className="deck-sub" style={{ marginTop: 8 }}>
          {status === "submitted"
            ? nachDemEinreichen
            : texte.liveStand}
        </p>
      </header>

      {!fields ? (
        <div
          style={{
            padding: "2rem 1rem",
            textAlign: "center",
            display: "grid",
            gap: 8,
          }}
        >
          <p className="deck-sub" style={{ margin: 0 }}>
            Schriftführer ist noch dabei einzurichten — gleich wird hier was
            stehen.
          </p>
        </div>
      ) : (
        fieldDefs.map(({ key, label, placeholder }, i) => {
          const value = fields[key];
          return (
            <div
              key={key}
              className={`req-readonly rise rise-${Math.min(i + 1, 6)}`}
            >
              <div className="req-readonly-label">{label}</div>
              <div className="req-field-hint">{placeholder}</div>
              <div
                className={`req-readonly-text${value ? "" : " is-empty"}`}
              >
                {value || "—"}
              </div>
            </div>
          );
        })
      )}
    </div>
  );
}
